import { Link } from "@tanstack/react-router"
import { Input } from "design-system/atoms/Input"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { GmailAccountFormSelector } from "modules/campaigns/components/Sections/triggers/gmail/GmailAccountFormSelector"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import { useFormContext } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"
import { getGmailIntegrationIdsFromTriggerIntegration } from "schema"
import { z } from "zod"
import { OpeningEmailForm } from "./OpeningEmailForm"
import {
  clearCampaignTriggerEmail,
  getCampaignTriggerEmail,
  replaceCampaignTriggerEmail,
} from "./campaignTriggerEmail"

type Props = {
  isDisabled: boolean
}

type FormValues = z.infer<typeof UpdateCampaignSchemaFrontend>
type Gmail = Extract<NonNullable<FormValues["triggerIntegration"]>, { type: "gmail" }>

export function GmailChannelForm({ isDisabled }: Props) {
  const { t } = useTranslation()
  const form = useFormContext<FormValues>()
  const triggerIntegration = form.watch("triggerIntegration")
  const gmailIntegrationIds = getGmailIntegrationIdsFromTriggerIntegration(triggerIntegration)
  const gmail = triggerIntegration?.type === "gmail" ? triggerIntegration : undefined
  const emailIntegration = getCampaignTriggerEmail(triggerIntegration)

  const buildGmail = (patch: Partial<Gmail>) =>
    replaceCampaignTriggerEmail(triggerIntegration, {
      type: "gmail",
      gmailIntegrationId: gmailIntegrationIds[0],
      gmailIntegrationIds,
      delayInDays: gmail?.delayInDays ?? undefined,
      ...(emailIntegration?.type === "gmail" ? emailIntegration : {}),
      ...patch,
    })

  return (
    <>
      <FormField
        control={form.control}
        name="triggerIntegration"
        render={({ field }) => {
          return (
            <div>
              <FormItem className="flex flex-col gap-2">
                <FormLabel>{t("campaigns.trigger.metadata.emailInbox.selectAccount")}</FormLabel>
                <FormControl>
                  <GmailAccountFormSelector
                    values={gmailIntegrationIds}
                    emptyPlaceholder={t("campaigns.trigger.metadata.emailInbox.emptyPlaceholder")}
                    placeholder={t("campaigns.trigger.metadata.emailInbox.emptyPlaceholder")}
                    searchPlaceholder={t("campaigns.trigger.metadata.emailInbox.searchPlaceholder")}
                    onChangeMany={(accounts) => {
                      const [firstAccount] = accounts
                      field.onChange(
                        firstAccount
                          ? buildGmail({
                              gmailIntegrationId: firstAccount.value,
                              gmailIntegrationIds: accounts.map((account) => account.value),
                            })
                          : clearCampaignTriggerEmail(triggerIntegration),
                        { shouldDirty: true },
                      )
                    }}
                    hasCreateNew
                    isDisabled={isDisabled}
                  />
                </FormControl>
                <FormMessage />
                <span className="text-sm text-muted-foreground">
                  <Trans
                    i18nKey={"campaigns.trigger.metadata.emailInbox.accountDescription"}
                    components={{
                      a: (
                        <Link
                          className="underline text-muted-foreground"
                          to="/settings/channels/gmail"
                          target="_blank"
                        />
                      ),
                    }}
                  />
                </span>
              </FormItem>
            </div>
          )
        }}
      />
      <FormField
        control={form.control}
        name="triggerIntegration"
        render={({ field }) => (
          <div>
            <FormItem className="flex flex-col gap-1">
              <FormLabel>
                {t("campaigns.trigger.metadata.emailInbox.disableAfterHumanInteraction.title")}
              </FormLabel>
              <FormControl>
                <div className="flex items-start gap-3">
                  <span className="text-muted-foreground text-xs block flex-1">
                    {t(
                      "campaigns.trigger.metadata.emailInbox.disableAfterHumanInteraction.description",
                    )}
                  </span>
                  <Input
                    wrapperClassName="w-auto"
                    className="w-24 shrink-0"
                    disabled={isDisabled}
                    value={gmail?.delayInDays ?? ""}
                    min={1}
                    type="number"
                    placeholder={t(
                      "campaigns.trigger.metadata.emailInbox.disableAfterHumanInteraction.placeholder",
                    )}
                    onChange={(value) => {
                      const delayInDays = value.target.value
                        ? Number(value.target.value)
                        : undefined

                      field.onChange(buildGmail({ delayInDays }), { shouldDirty: true })
                    }}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          </div>
        )}
      />
      <OpeningEmailForm isDisabled={isDisabled} type="gmail" />
    </>
  )
}
